import { FREE_LISTING_TYPE } from './configListing';

//////////////////////////////////////////////////////////////////////
// Configurations related to search                                 //
// Note: some search experience is also on defaultMapsConfig        //
// and defaultListingConfig.js is responsible public data filtering //
//////////////////////////////////////////////////////////////////////

// NOTE: if you want to change the structure of the data,
// you should also check src/util/configHelpers.js
// some validation is added there.

// Main search used in Topbar.
// This can be either 'keywords' or 'location'.
// Note: The mainSearch comes from the listing-search asset nowadays by default.
//       To use this built-in configuration, you need to remove the overwrite from configHelper.js (mergeSearchConfig func)
export const mainSearch = {
  searchType: 'location',
};

/**
 * Configuration for default filters.
 * These are custom configs for each filter.
 * Common properties: key, schemaType, and label.
 * Note: the order of default filters is defined in util/configHelpers.js
 * To use this built-in configuration, you need to remove the overwrite from configHelper.js (mergeSearchConfig func)
 */

export const listingTypeFilter = {
  enabled: false,
  schemaType: 'listingType',
  // schemaType, key, and other built-in config values are completely filled in configHelper.js
};

export const categoryFilter = {
  enabled: false,
  schemaType: 'category',
  // schemaType, key, and other built-in config values are completely filled in configHelper.js
};

export const dateRangeFilter = {
  schemaType: 'dates',
  // Should the entire date range be available, or just part of it
  // Note: Since we don't enforce location search for dates filtering,
  //       we don't use API's 'time-full' in actual queries. It would require time zone info.
  availability: 'time-full', // time-partial
  // Options: day/night. This affects counting and whether single day picking is possible.
  dateRangeMode: 'day',
};

/**
 * Note: the order of default filters is defined in util/configHelpers.js
 * To use this built-in configuration, you need to remove the overwrite from configHelper.js (mergeSearchConfig func)
 */
export const priceFilter = {
  schemaType: 'price',
  // Note: unlike most prices this is not handled in subunits
  min: 0,
  max: 1000,
  step: 5,
};
// // This is not in use by default.
// export const keywordsFilter = {
//   key: 'keywords',
//   schemaType: 'keywords',
// }

export const sortConfig = {
  // Enable/disable the sorting control in the SearchPage
  active: true,

  // Note: queryParamName 'sort' is fixed,
  // you can't change it since Marketplace API expects it to be named as 'sort'
  queryParamName: 'sort',

  // Internal key for the relevance option, see notes below.
  relevanceKey: 'relevance',

  // Relevance key is used with keywords filter.
  // Keywords filter also sorts results according to relevance.
  relevanceFilter: 'keywords',

  // Keyword filter is sorting the results by relevance.
  // If keyword filter is active, one might want to disable other sorting options
  // by adding 'keywords' to this list.
  conflictingFilters: [],

  options: [
    // DATAMODEL-01: Uroborox no usa Precio. Tras el rediseño del modelo
    // de datos, EBITDA desaparece y `facturacion` pasa a enum por
    // tramos — la ordenación por enum es alfabética (no semántica),
    // así que sólo se mantiene `createdAt` como sort meaningful.
    //
    // Convención Sharetribe (consistente con el template original
    // `-price`→'lowest', `price`→'highest', `createdAt`→'newest',
    // `-createdAt`→'oldest'): SIN prefijo = descendente, CON prefijo
    // '-' = ascendente.
    // FASE2-BUSCADOR-3e: `listingTypes` acota una opción a unos feeds. Sin la
    // clave, la opción vale para todos — que es el caso de las dos de fecha.
    { key: 'createdAt', labelTranslationKey: 'SortBy.newest' },
    { key: '-createdAt', labelTranslationKey: 'SortBy.oldest' },

    // FACTURACION-IDX-01 (3b): ordenación por facturación (vía el índice
    // ordinal pub_facturacionIdx, long 1..10 — el enum no es ordenable)
    // y por empleados (pub_empleados, long). Misma convención que arriba:
    // sin prefijo = descendente, '-' = ascendente. Van DESPUÉS de las de
    // fecha para que el default siga siendo "Más nuevo" (primera opción).
    //
    // FASE2-BUSCADOR-3e: solo en el feed de venta. El mandato guarda
    // INTERVALOS (facturacionIdxMin/Max), no un valor, y ordenar por el
    // extremo de un rango no dice nada útil: un mandato que acepta "de 0 a
    // 25 M€" saldría el primero por tener el techo más alto, sin que eso
    // signifique que busca negocios grandes. Además `pub_facturacionIdx` y
    // `pub_empleados` ni siquiera existen en un mandato, así que la API los
    // devolvía en orden arbitrario y en silencio.
    {
      key: 'pub_facturacionIdx',
      labelTranslationKey: 'SortBy.facturacionHigh',
      listingTypes: [FREE_LISTING_TYPE],
    },
    {
      key: '-pub_facturacionIdx',
      labelTranslationKey: 'SortBy.facturacionLow',
      listingTypes: [FREE_LISTING_TYPE],
    },
    {
      key: 'pub_empleados',
      labelTranslationKey: 'SortBy.empleadosHigh',
      listingTypes: [FREE_LISTING_TYPE],
    },
    {
      key: '-pub_empleados',
      labelTranslationKey: 'SortBy.empleadosLow',
      listingTypes: [FREE_LISTING_TYPE],
    },

    // P8B.1: 'Relevancia' eliminada — Uroborox es un marketplace de negocios,
    // no un buscador con ranking semántico por keywords. Mantenemos
    // relevanceKey/relevanceFilter arriba porque configHelpers y SortBy.js
    // hacen lookups por ese key (no rompe si no hay match en options).
  ],
};

/**
 * FASE2-BUSCADOR-3e — Las opciones de ordenación que aplican a un feed.
 *
 * Una opción sin `listingTypes` vale para todos los feeds; con la clave, solo
 * para los que enumera. Se usa en dos sitios y por dos motivos distintos: para
 * pintar el selector, y para decidir si el `sort` que trae la URL es válido
 * aquí — alguien puede llegar con un enlace guardado de cuando el feed tenía
 * más opciones, o de otro feed.
 *
 * @param {Object} config sortConfig (o el de Console, con la misma forma)
 * @param {string} listingType tipo de anuncio del feed; sin él, no se acota
 * @returns {Object} el mismo config con `options` filtradas
 */
export const sortConfigForListingType = (config, listingType) => {
  const options = config?.options || [];
  if (!listingType) {
    return config;
  }
  return {
    ...config,
    options: options.filter(o => !o.listingTypes || o.listingTypes.includes(listingType)),
  };
};

/**
 * ¿Vale este `sort` en este feed? Un sort que no aplica no vacía el listado:
 * la API ordena por un campo que esos anuncios no tienen y devuelve un orden
 * arbitrario, en silencio. Por eso hay que descartarlo explícitamente.
 *
 * @returns {boolean} true si la URL no trae sort, o si el que trae es válido
 */
export const isValidSortForListingType = (config, listingType, sortValue) => {
  if (!sortValue) {
    return true;
  }
  const { options } = sortConfigForListingType(config, listingType);
  return options.some(o => o.key === sortValue);
};

